import {
  Fragment,
} from 'react';
import Text from '@/ui/components/Text';

import styles from './page.module.css';

const TEXT_BLOCKS = [
  {
    id: 'introduction',
    title: '1. Introduction',
    subtitle: 'These Terms of Use (“Terms”) are a legally binding agreement between you \
               (“you”, “your”, or “User”) and Dinosave (“we”, “us”, or “our”) concerning your \
               access to and use of the Dinosave application and associated services. \
               By downloading, installing, registering with or otherwise using Dinosave, you agree \
               to be bound by these Terms. If you do not agree, do not access or use Dinosave.',
  },
  {
    id: 'definitions',
    title: '2. Definitions',
    text: [
      '“Account” means an individual account registered by you to access the App.',
      '“Content” means any data, text, images, links, items, wishlists, \
      comments or other material you create, upload or submit via the App.',
      '“Service” means the features provided through Dinosave, including but not limited \
      to wishlists creation, item price tracking, sharing, notifications, and any other functionality.',
      '“You/Your” means the user of Dinsoave, an individual or entity who uses the App.',
    ],
  },
  {
    id: 'eligibility',
    title: '3. Eligibility',
    subtitle: 'You represent and warrant that you are at least the minimum legal age in \
    your jurisdiction (e.g., at least 13 years old, or older if required) to form a binding \
    contract and to use the Service. If you are accessing Dinosave on behalf of an entity, \
    you further represent that you have the authority to agree to these Terms on behalf of \
    that entity.',
  },
  {
    id: 'license',
    title: '4. License',
    subtitle: 'Subject to your compliance with these Terms, we grant you a limited, \
    non-exclusive, non-transferable, revocable license to download, install and use \
    one copy of the App on your device for your personal, non-commercial use.',
  },
  {
    id: 'affiliate_disclosure',
    title: '5. Affiliate Disclosure',
    subtitle: 'Dinosave uses affiliate programs for monetisation. This means Dinosave \
    may receive a commission when you purchase a product through our outbound links. \
    Affiliate programs and affiliations include, but are not limited to, Amazon Associates, \
    Skimlinks, Taboola, Sovrn and AWIN.',
  },
  {
    id: 'user_accounts',
    title: '6. User Accounts',
    text: [
      'You may be required to register for an Account to access some or all features of Dinosave.',
      'You must provide accurate and complete information and keep your Account credentials secure.',
      'You are responsible for any activity that occurs under your Account.',
      'You must notify us immediately if you suspect unauthorized access or a breach of your Account.',
    ],
  },
  {
    id: 'user_content',
    title: '7. User Content',
    text: [
      'You retain ownership of your Content.',
      'You hereby grant us a worldwide, royalty-free, non-exclusive license to use, reproduce, \
      modify, adapt, publish and display your Content in connection with providing the Service.',
      'You represent and warrant that you own all rights in the Content you upload or otherwise \
      submit, and that such Content does not violate any person’s rights or any applicable law.',
      'You may share wishlists or items with other users or third parties, at your direction. \
      We are not responsible for third-party use or misuse of shared Content.',
      'We reserve the right (but have no obligation) to monitor, review, refuse, remove or \
      disable access to any Content for any reason, including if we believe it violates these Terms.',
    ],
  },
  {
    id: 'acceptable_use_prohibited_conduct',
    title: '7. Acceptable Use / Prohibited Conduct',
    subtitle: 'You agree not to use the Service to:',
    text: [
      'Violate any applicable law or regulation.',
      'Infringe any person’s intellectual property or other rights.',
      'Post or transmit any harmful, abusive, harassing, defamatory, obscene, fraudulent or offensive Content.',
      'Interfere with or disrupt the Service or servers/networks connected to the Service.',
      'Attempt to gain unauthorized access to the Service or other users’ accounts.',
      'Use automated scripts or bots in connection with the Service, unless explicitly permitted.',
      'Use the Service for commercial purposes unless we have explicitly permitted you to do so.',
    ],
  },
  {
    id: 'payments_subscriptions',
    title: '8. Payments / Subscriptions',
    subtitle: 'Dinosave offers paid features via a subscription:',
    text: [
      'You agree to pay all applicable fees if you wish to purchase a subscription.',
      'We may change pricing or fees at any time. Any such changes will be communicated to you.',
      'We may suspend, downgrade or terminate paid features for non-payment.',
    ],
  },
  {
    id: 'intellectual_property',
    title: '9. Intellectual Property',
    subtitle: 'All rights, title and interest in and to Dinosave (excluding your User Content) \
    and all intellectual property rights therein are owned by us or our licensors. You may not copy, \
    modify, distribute, sell or lease any part of Dinosave or included software, nor reverse engineer \
    or attempt to extract the source code, unless permitted by law.',
  },
  {
    id: 'disclaimers_of_warranties',
    title: '10. Disclaimers of Warranties',
    subtitle: 'The Service is provided “as-is” and “as available”. We disclaim all warranties of \
    any kind, whether express or implied, to the fullest extent permitted by applicable law, \
    including without limitation:',
    text: [
      'That the Service will be uninterrupted, error-free or secure.',
      'That any Content is accurate, reliable or trustworthy.',
      'That the Service meets your requirements or expectations.',
    ],
  },
  {
    id: 'limitation_of_liability',
    title: '11. Limitation of Liability',
    subtitle: 'To the fullest extent permitted by law, in no event shall we, our affiliates, \
    officers, employees, agents or licensors be liable for any indirect, incidental, special, \
    consequential or punitive damages, including but not limited to loss of profits, data or \
    goodwill, arising out of or in connection with your use or inability to use the Service, \
    even if we have been advised of the possibility of such damages. Our total liability for any \
    claim arising under these Terms shall not exceed the amount you paid (if any) for the Service \
    in the past 12 months.',
  },
  {
    id: 'termination',
    title: '12. Termination',
    subtitle: 'We may suspend or terminate your Account or access to the Service, \
    for any reason and at any time, without notice or liability. Upon termination:',
    text: [
      'Your right to use the Service will immediately cease.',
      'Any licenses granted to you will end.',
      'We may delete or disable access to your Content.',
      'You may also terminate by deleting your Account.',
    ],
  },
  {
    id: 'terms_updates',
    title: '13. Terms Updates',
    subtitle: 'We may revise these Terms at any time. If we do so, we will post the updated \
    Terms on our website and update the “Last updated” date. By continuing to use the Service \
    after such changes become effective, you agree to the revised Terms. If you do not agree to \
    the new Terms, you must stop using the Service.',
  },
  {
    id: 'governing_law_and_dispute_resolution',
    title: '14. Governing Law and Dispute Resolution',
    subtitle: 'These Terms shall be governed by and construed in accordance with the laws \
    of Poland, without regard to its conflict-of-law provisions. You agree that all disputes \
    arising out of or related to these Terms or the Service shall be resolved exclusively in \
    the courts of Poland, and you consent to personal jurisdiction in those courts.',
  },
  {
    id: 'severability',
    title: '15. Severability',
    subtitle: 'If any provision of these Terms is held invalid or unenforceable for any reason, \
    the remaining provisions shall continue in full force and effect.',
  },
  {
    id: 'entire_agreement',
    title: '16. Entire Agreement',
    subtitle: 'These Terms, together with our Privacy Policy and any other policies referenced \
    herein or accessible via the Service, constitute the entire agreement between you and us \
    regarding your use of the Service, superseding any prior or contemporaneous agreements, \
    communications or proposals.',
  },
  {
    id: 'contact',
    title: '17. Contact',
    subtitle: (
      <Fragment key="contact">
        We welcome feedback and support inquiries. Users can directly reach out to our
        team at
        <a href="mailto:feedback@dinosave.com">
          <Text weight="500">
            &nbsp;feedback@dinosave.co&nbsp;
          </Text>
        </a>
        for any questions, suggestions, or concerns.
      </Fragment>
    ),
  },
];

function TermsPage() {
  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <div className={styles.termsPage}>
      <Text
        kind="super"
        className={styles.title}
      >
        Terms of Use
      </Text>

      <Text
        kind="paragraph"
        size="large"
        weight="300"
        className={styles.subtitle}
      >
        Last updated May 3, 2026
      </Text>

      {TEXT_BLOCKS.map((block, index) => (
        <div
          id={block.id}
          key={index}
          className={styles.block}
        >
          <Text
            kind="heading-4"
            weight="600"
            className={styles.blockTitle}
          >
            {block.title}
          </Text>

          <ul className={styles.blockText}>
            {block.subtitle && (
              <Text
                kind="paragraph"
                size="regular"
                weight="300"
                className={styles.blockTextSubtitle}
              >
                {block.subtitle}
              </Text>
            )}

            {block.text?.map((text, index) => (
              <li
                key={index}
                className={styles.listItem}
              >
                <Text
                  kind="paragraph"
                  size="regular"
                  weight="300"
                >
                  {text}
                </Text>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default TermsPage;
